import { buildUnifiedAccount } from 'unified-auth-core-lib';

import microsoftDriveTranslations from 'i2l?mode=very-lazy&query=sporks!microsoft-drive-auth-lib/lang/en.lyaml';
import customVendorConnectionTranslations from 'i2l?mode=very-lazy&query=sporks!custom-vendor-connection-lib/lang/en.lyaml';

import type { AuthRegistry } from './registry';
import { DEFAULT_PROVIDER_KEY, PROVIDER_KEYS } from './registry';
import {
  getAdobeSignAppId,
  getAppIdByAppName,
  getChatGptAdsAppId,
  getCohereAppId,
  getCustomVendorConnectionAppId,
  getGeminiAppId,
  getGoogleSheetsAppId,
  getMicrosoftAdsAppId,
  getMicrosoftDriveAppId,
  getRedditAdsAppId,
  getSlackIntegrationAppId,
  getSurveyMonkeyAppId,
  getXAiAppId,
  getZoomInfoAppId,
  INTEGRATIONS_DASHWORKS,
  INTEGRATIONS_MCP,
} from '../constants/appIds';

const getDashworksAppIds = () =>
  Object.values(INTEGRATIONS_DASHWORKS)
    .map(getAppIdByAppName)
    .filter((id) => id !== undefined);

const getMcpAppIds = () =>
  Object.values(INTEGRATIONS_MCP)
    .map(getAppIdByAppName)
    .filter((id) => id !== undefined);

export function initializeDefaults(registry: AuthRegistry): void {
  const DefaultComponents = buildUnifiedAccount({
    displayName: 'UnifiedAccount',
  });

  registry.registerProvider(DEFAULT_PROVIDER_KEY, {
    components: DefaultComponents,
  });
  registry.registerProvider(PROVIDER_KEYS.UNIFIED_ACCOUNT, {
    components: DefaultComponents,
    overwrite: true,
  });

  registry.registerProvider(PROVIDER_KEYS.DASHWORKS, {
    appIds: getDashworksAppIds(),
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-dashworks" */ 'ui-universal-auth/providers/dashworks'
      ).then((m) => m.DashworksAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.ZOOMINFO, {
    appIds: [getZoomInfoAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-zoominfo" */ 'ui-universal-auth/providers/zoominfo'
      ).then((m) => m.ZoomInfoAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.MICROSOFT_DRIVE, {
    appIds: [getMicrosoftDriveAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-microsoft-drive" */ 'microsoft-drive-auth-lib/auth'
      ).then((m) => m.MicrosoftDriveAuth),
    lazyLoadedTranslations: microsoftDriveTranslations,
  });

  registry.registerProvider(PROVIDER_KEYS.MICROSOFT_ADS, {
    appIds: [getMicrosoftAdsAppId()],
    load: () =>
      import('ads-unified-auth-lib/auth/microsoft').then(
        (m) => m.MicrosoftAdsAuth
      ),
  });

  registry.registerProvider(PROVIDER_KEYS.GOOGLE_SHEETS, {
    appIds: [getGoogleSheetsAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-google-sheets" */ 'google-sheets-integration-lib/auth'
      ).then((m) => m.GoogleSheetsAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.SLACK_INTEGRATION, {
    appIds: [getSlackIntegrationAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-slack-integration" */ 'slack-ui-library/auth'
      ).then((m) => m.SlackAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.SURVEY_MONKEY, {
    appIds: [getSurveyMonkeyAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-survey-monkey" */ 'surveymonkey-integration-lib/auth'
      ).then((m) => m.SurveyMonkeyAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.REDDIT_ADS, {
    appIds: [getRedditAdsAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-reddit-ads" */ 'ads-unified-auth-lib/auth/reddit'
      ).then((m) => m.RedditAdsAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.CHATGPT_ADS, {
    appIds: [getChatGptAdsAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-chatgpt-ads" */ 'ads-unified-auth-lib/auth/chatgpt'
      ).then((m) => m.ChatGptAdsAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.ADOBE_SIGN, {
    appIds: [getAdobeSignAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-adobe-sign" */ 'adobe-sign-auth-lib/auth'
      ).then((m) => m.AdobeSignAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.CUSTOM_VENDOR_CONNECTION, {
    appIds: [getCustomVendorConnectionAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-custom-vendor-connection" */ 'custom-vendor-connection-lib/auth'
      ).then((m) => m.CustomVendorConnectionWizardAuth),
    lazyLoadedTranslations: customVendorConnectionTranslations,
  });

  registry.registerProvider(PROVIDER_KEYS.GEMINI, {
    appIds: [getGeminiAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-gemini" */ 'gemini-settings-lib/auth'
      ).then((m) => m.GeminiAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.COHERE, {
    appIds: [getCohereAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-cohere" */ 'cohere-settings-lib/auth'
      ).then((m) => m.CohereAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.X_AI, {
    appIds: [getXAiAppId()],
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-x-ai" */ 'x-ai-settings-lib/auth'
      ).then((m) => m.XAiAuth),
  });

  registry.registerProvider(PROVIDER_KEYS.MCP, {
    appIds: getMcpAppIds(),
    load: () =>
      import(
        /* webpackChunkName: "unified-auth-mcp" */ 'ai-mcp-auth-lib/auth'
      ).then((m) => m.McpAuth),
  });
}
